'use client'

import { EntityCatalogTab } from '@/components/catalog/EntityCatalogTab'
import AddressList from '@/components/address/address-list'
import { PayoutScheduleDisplay } from '@/components/payout-schedule/PayoutScheduleDisplay'
import { getHospital, Hospital } from '@/lib/api/hospitals/hospital-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdBusiness,
    MdCalendarToday,
    MdEdit,
    MdEmail,
    MdInventory2,
    MdLanguage,
    MdLocalHospital,
    MdLocationOn,
    MdNotes,
    MdPhone,
    MdToggleOff,
    MdToggleOn,
} from 'react-icons/md'
import { FaUserDoctor } from 'react-icons/fa6'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import NotificationPreferencesCard from '@/components/notification-preferences/notification-preferences-card'

const formatCnpj = (cnpj: string): string => {
    const digits = cnpj.replace(/\D/g, '')
    if (digits.length !== 14) return cnpj
    return digits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5',
    )
}

export default function HospitalDetailPage() {
    const navigate = useNavigate()
    const { canUpdate } = useResourcePermissions('hospital', 'hospitals')
    const { user } = useAuth()
    const { id } = useParams<{ id: string }>()
    const [hospital, setHospital] = useState<Hospital | null>(null)
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState<'dados' | 'catalog'>('dados')

    useEffect(() => {
        if (id) {
            loadHospital()
        }
    }, [id])

    const loadHospital = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getHospital(parseInt(id, 10))
            if (response.success && response.result) {
                setHospital(response.result)
            } else {
                toast.error(
                    response.error || 'Erro ao carregar hospital',
                )
                navigate('/registrations/hospitals')
            }
        } catch {
            toast.error('Erro ao carregar hospital')
            navigate('/registrations/hospitals')
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando detalhes do hospital...
                </p>
            </div>
        )
    }

    if (!hospital) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                    <MdLocalHospital className="text-4xl text-base-content/30" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        Hospital não encontrado
                    </h3>
                    <p className="text-sm text-base-content/60">
                        O hospital que você está procurando não existe
                    </p>
                </div>
                <button
                    onClick={() => navigate('/registrations/hospitals')}
                    className="btn btn-secondary"
                >
                    Voltar para Hospitais
                </button>
            </div>
        )
    }

    const professionals = hospital.professionals || []

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/registrations/hospitals')}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 hidden md:flex rounded-xl bg-gradient-to-br from-secondary to-secondary/70 items-center justify-center shadow-lg">
                            <MdLocalHospital className="text-2xl text-white" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                    {hospital.name}
                                </h1>
                                <span
                                    className={`badge badge-sm ${
                                        hospital.active
                                            ? 'badge-success'
                                            : 'badge-error'
                                    }`}
                                >
                                    {hospital.active ? 'Ativo' : 'Inativo'}
                                </span>
                            </div>
                            {hospital.tradingName && (
                                <p className="text-sm text-base-content/60">
                                    {hospital.tradingName}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                {canUpdate && (
                    <button
                        onClick={() =>
                            navigate(`/registrations/hospitals/edit/${hospital.id}`)
                        }
                        className="btn btn-secondary gap-2"
                    >
                        <MdEdit className="text-lg" />
                        <span className="hidden sm:inline">Editar</span>
                    </button>
                )}
            </div>

            {/* Abas */}
            <div role="tablist" className="tabs tabs-bordered">
                <button
                    role="tab"
                    className={`tab gap-2 ${activeTab === 'dados' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('dados')}
                >
                    <MdBusiness className="text-lg" /> Dados
                </button>
                <button
                    role="tab"
                    className={`tab gap-2 ${activeTab === 'catalog' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('catalog')}
                >
                    <MdInventory2 className="text-lg" /> Catálogo do Hospital
                </button>
            </div>

            {activeTab === 'catalog' &&
                (user?.companyId ? (
                    <EntityCatalogTab
                        entityType="hospital"
                        entityId={parseInt(id!, 10)}
                        companyId={user.companyId}
                    />
                ) : (
                    <p className="text-center py-10 text-base-content/60">
                        Empresa do usuário não identificada.
                    </p>
                ))}

            {/* Datas */}
            <div
                className={`bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden ${
                    activeTab === 'dados' ? '' : 'hidden'
                }`}
            >
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 text-sm text-base-content/70">
                            <MdCalendarToday />
                            <span>
                                Criado em{' '}
                                {format(
                                    new Date(hospital.createdAt),
                                    "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                    { locale: ptBR },
                                )}
                            </span>
                        </div>
                        {hospital.updatedAt !== hospital.createdAt && (
                            <div className="flex items-center gap-2 text-sm text-base-content/70">
                                <span>
                                    Atualizado em{' '}
                                    {format(
                                        new Date(hospital.updatedAt),
                                        "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                        { locale: ptBR },
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-6">
                    {/* Dados Gerais */}
                    <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                        <MdBusiness className="text-secondary" />
                        Dados Gerais
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                        {hospital.cnpj && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdBusiness className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        CNPJ
                                    </p>
                                    <p className="text-sm font-semibold text-base-content font-mono">
                                        {formatCnpj(hospital.cnpj)}
                                    </p>
                                </div>
                            </div>
                        )}

                        {hospital.cnes && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdLocalHospital className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        CNES
                                    </p>
                                    <p className="text-sm font-semibold text-base-content">
                                        {hospital.cnes}
                                    </p>
                                </div>
                            </div>
                        )}

                        {hospital.email && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-info/10 rounded-lg">
                                    <MdEmail className="text-xl text-info" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Email
                                    </p>
                                    <a
                                        href={`mailto:${hospital.email}`}
                                        className="text-sm font-semibold text-info hover:underline break-all"
                                    >
                                        {hospital.email}
                                    </a>
                                </div>
                            </div>
                        )}

                        {hospital.phone && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-success/10 rounded-lg">
                                    <MdPhone className="text-xl text-success" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Telefone
                                    </p>
                                    <p className="text-sm font-semibold text-base-content">
                                        {hospital.phone}
                                    </p>
                                </div>
                            </div>
                        )}

                        {hospital.website && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-accent/10 rounded-lg">
                                    <MdLanguage className="text-xl text-accent" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Website
                                    </p>
                                    <a
                                        href={
                                            hospital.website.startsWith('http')
                                                ? hospital.website
                                                : `https://${hospital.website}`
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-semibold text-accent hover:underline break-all"
                                    >
                                        {hospital.website}
                                    </a>
                                </div>
                            </div>
                        )}

                        <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                            <div className="p-2 bg-base-300/50 rounded-lg">
                                {hospital.active ? (
                                    <MdToggleOn className="text-xl text-success" />
                                ) : (
                                    <MdToggleOff className="text-xl text-error" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-base-content/60 mb-1">
                                    Status
                                </p>
                                <p className="text-sm font-semibold text-base-content">
                                    {hospital.active ? 'Ativo' : 'Inativo'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Endereços */}
                    <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                        <MdLocationOn className="text-warning" />
                        Endereços
                    </h3>
                    <div className="p-4 bg-base-200/50 rounded-xl mb-6">
                        <AddressList
                            owner={{ hospitalId: hospital.id }}
                            emptyHint="Nenhum endereço cadastrado para este hospital"
                        />
                    </div>

                    {/* Repasse de Valores */}
                    <div className="mb-6">
                        <PayoutScheduleDisplay
                            payoutScheduleType={hospital.payoutScheduleType}
                            payoutFixedDay={hospital.payoutFixedDay}
                            payoutDaysAfterAppointment={hospital.payoutDaysAfterAppointment}
                            title="Repasse de Valores ao Hospital"
                        />
                    </div>

                    {/* Descrição */}
                    {hospital.description && (
                        <>
                            <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                                <MdNotes className="text-secondary" />
                                Descrição
                            </h3>
                            <div className="p-4 bg-base-200/50 rounded-xl mb-6">
                                <p className="text-sm text-base-content whitespace-pre-wrap">
                                    {hospital.description}
                                </p>
                            </div>
                        </>
                    )}

                    {/* Profissionais Vinculados */}
                    <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                        <FaUserDoctor className="text-secondary" />
                        Profissionais Vinculados
                        {professionals.length > 0 && (
                            <span className="badge badge-secondary badge-sm">
                                {professionals.length}
                            </span>
                        )}
                    </h3>
                    {professionals.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {professionals.map(ph => (
                                <div
                                    key={ph.id}
                                    className="flex items-center gap-3 p-4 bg-base-200/50 rounded-xl hover:bg-base-200/70 transition-colors cursor-pointer"
                                    onClick={() => navigate(`/person/professionals/${ph.professional.id}`)}
                                >
                                    <div className="p-2 bg-secondary/10 rounded-lg">
                                        <FaUserDoctor className="text-lg text-secondary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-base-content truncate">
                                            {ph.professional.name}
                                        </p>
                                        {(ph.professional.councilName || ph.professional.registrationNumber) && (
                                            <p className="text-xs text-base-content/60">
                                                {[ph.professional.councilName, ph.professional.registrationNumber]
                                                    .filter(Boolean)
                                                    .join(' - ')}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-6 text-base-content/60 bg-base-200/30 rounded-xl">
                            <p className="text-sm">
                                Nenhum profissional vinculado a este hospital
                            </p>
                        </div>
                    )}


                    {/* Notificações: por quais canais este hospital aceita ser avisado.
                        Fica no detalhe, e não na edição, porque é decisão de
                        relacionamento — e vale na hora, sem salvar a tela inteira. */}
                    <NotificationPreferencesCard
                        entityType="hospital"
                        entityId={hospital.id}
                        preferences={hospital.notificationPreferences}
                        entityName={hospital.name}
                        canEdit={canUpdate}
                    />

                    {/* Sem informações */}
                    {!hospital.cnpj &&
                        !hospital.cnes &&
                        !hospital.email &&
                        !hospital.phone &&
                        !hospital.website &&
                        !hospital.description &&
                        professionals.length === 0 && (
                            <div className="text-center py-8 text-base-content/60">
                                <p>
                                    Nenhuma informação adicional disponível
                                </p>
                            </div>
                        )}
                </div>
            </div>
        </div>
    )
}
